import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import RoleBadge from '../components/ui/RoleBadge';

const { FiArrowLeft, FiExternalLink, FiMail, FiMapPin, FiCalendar, FiUsers, FiTarget, FiBriefcase } = FiIcons;

const ProfileDetail = () => {
  const { id } = useParams();
  const { profiles, createMatchRequest } = useData();
  const { user } = useAuth();

  const profile = profiles.find(p => p.id === id);

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Profile Not Found</h2>
          <p className="text-gray-600 mb-6">The profile you're looking for doesn't exist.</p>
          <Link
            to="/dashboard"
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const handleRequestIntro = () => {
    createMatchRequest(user.id, profile.id, 'Hi! I\'d like to connect and explore potential collaboration opportunities.');
    alert('Introduction request sent!');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link
          to="/dashboard"
          className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          <span>Back to Dashboard</span>
        </Link>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-12">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between">
              <div className="flex items-start space-x-4">
                {/* Logo/Avatar */}
                {profile.logo ? (
                  <div className="w-20 h-20 rounded-xl overflow-hidden border-4 border-white shadow-lg flex-shrink-0">
                    <img
                      src={profile.logo}
                      alt={`${profile.company || profile.name} logo`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-20 h-20 bg-white bg-opacity-20 rounded-xl flex items-center justify-center border-4 border-white shadow-lg flex-shrink-0">
                    <span className="text-white font-bold text-2xl">
                      {(profile.name || '').charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}

                <div>
                  <h1 className="text-3xl font-bold text-white mb-2">
                    {profile.name}
                  </h1>
                  {profile.company && (
                    <p className="text-indigo-100 text-lg mb-3">{profile.company}</p>
                  )}
                  <RoleBadge role={profile.role} />
                </div>
              </div>

              <div className="mt-4 sm:mt-0">
                <button
                  onClick={handleRequestIntro}
                  className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors inline-flex items-center space-x-2"
                >
                  <SafeIcon icon={FiMail} className="w-5 h-5" />
                  <span>Request Introduction</span>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {/* Summary */}
                {profile.summary && (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">About</h2>
                    <p className="text-gray-600 leading-relaxed">{profile.summary}</p>
                  </div>
                )}

                {/* Needs & Offers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {profile.needs && profile.needs.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <SafeIcon icon={FiTarget} className="w-5 h-5 text-red-500" />
                        <span>Looking For</span>
                      </h3>
                      <div className="space-y-2">
                        {profile.needs.map(need => (
                          <span
                            key={need}
                            className="inline-block px-3 py-1 bg-red-50 text-red-700 text-sm rounded-full mr-2 mb-2"
                          >
                            {need}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {profile.offers && profile.offers.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <SafeIcon icon={FiUsers} className="w-5 h-5 text-green-500" />
                        <span>Can Offer</span>
                      </h3>
                      <div className="space-y-2">
                        {profile.offers.map(offer => (
                          <span
                            key={offer}
                            className="inline-block px-3 py-1 bg-green-50 text-green-700 text-sm rounded-full mr-2 mb-2"
                          >
                            {offer}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Details</h3>
                  <div className="space-y-3">
                    {profile.businessType && (
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiBriefcase} className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{profile.businessType}</span>
                      </div>
                    )}

                    {profile.email && (
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{profile.email}</span>
                      </div>
                    )}

                    {profile.website && (
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-gray-500" />
                        <a
                          href={profile.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-indigo-600 hover:text-indigo-700"
                        >
                          Website
                        </a>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        Joined {new Date(profile.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Platforms */}
                {profile.platforms && profile.platforms.length > 0 && (
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Platforms</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.platforms.map(platform => (
                        <span
                          key={platform}
                          className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Genres */}
                {profile.genres && profile.genres.length > 0 && (
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Genres</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.genres.map(genre => (
                        <span
                          key={genre}
                          className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full"
                        >
                          {genre}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Markets */}
                {profile.markets && profile.markets.length > 0 && (
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Target Markets</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.markets.map(market => (
                        <span
                          key={market}
                          className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full"
                        >
                          {market}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetail;